import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, Clock, AlertTriangle, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtErr = (d) => (typeof d === "string" ? d : d?.msg || "Something went wrong.");
const fmt = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

const UploadRow = ({ token, item, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const done = item.uploaded_now;
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/candidate-review/request/${token}/upload/${item.id}`, fd);
      toast.success(`${item.label} uploaded`);
      onDone();
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className={`flex items-center justify-between gap-3 border px-4 py-3 ${done ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/50 bg-amber-500/5"}`} data-testid={`request-item-${item.id}`}>
      <div className="min-w-0">
        <p className="text-sm text-white">{item.label}</p>
        {item.reason && <p className="text-[11px] text-slate-300 mt-0.5" data-testid={`request-reason-${item.id}`}>Reason: {item.reason}</p>}
        <p className="text-[11px] text-slate-400 mt-0.5">
          {done ? <span className="text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Uploaded {item.uploaded_at ? fmt(item.uploaded_at) : "just now"}</span> : <span className="text-amber-300">Missing — please upload</span>}
        </p>
      </div>
      <label className={`shrink-0 inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 border cursor-pointer ${busy ? "opacity-60" : ""} border-nx-gold/60 text-nx-goldlight hover:bg-nx-gold/10`} data-testid={`request-upload-${item.id}`}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {done ? "Replace" : "Upload"}
        <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={pick} disabled={busy} />
      </label>
    </div>
  );
};

const TextRow = ({ token, item, onDone }) => {
  const [value, setValue] = useState(item.value || "");
  const [busy, setBusy] = useState(false);
  const done = item.uploaded_now;
  const submit = async () => {
    if (!value.trim()) return toast.error("Please enter the requested details.");
    setBusy(true);
    try {
      await axios.post(`${API}/candidate-review/request/${token}/text/${item.id}`, { value: value.trim() });
      toast.success(`${item.label} submitted`);
      onDone();
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`border px-4 py-3 ${done ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/50 bg-amber-500/5"}`} data-testid={`request-item-${item.id}`}>
      <p className="text-sm text-white">{item.label}</p>
      {item.reason && <p className="text-[11px] text-slate-300 mt-0.5" data-testid={`request-reason-${item.id}`}>Reason: {item.reason}</p>}
      <p className="text-[11px] text-slate-400 mt-0.5">
        {done ? <span className="text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Provided {item.uploaded_at ? fmt(item.uploaded_at) : "just now"}</span> : <span className="text-amber-300">Missing — please fill in below</span>}
      </p>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={2} maxLength={1000} placeholder={`Enter your ${item.label.toLowerCase()}`}
          className="flex-1 bg-white/[0.04] border border-white/15 focus:border-nx-gold/60 outline-none text-sm text-white px-3 py-2 resize-y" data-testid={`request-text-${item.id}`} />
        <button onClick={submit} disabled={busy} className="shrink-0 inline-flex items-center justify-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-4 py-2 border border-nx-gold/60 text-nx-goldlight hover:bg-nx-gold/10 disabled:opacity-60" data-testid={`request-submit-${item.id}`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} {done ? "Update" : "Submit"}
        </button>
      </div>
    </div>
  );
};

export default function DocRequestUpload() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/candidate-review/request/${token}`);
      setData(data);
      setError("");
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail) || "This link is not valid.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!token) return <Navigate to="/" replace />;
  const allDone = data?.status === "fulfilled";

  return (
    <div className="min-h-screen bg-nx-navy flex items-center justify-center px-4 py-12" data-testid="doc-request-page">
      <div className="w-full max-w-xl">
        <img src="/assets/logo-signature-dark.png" alt="NexSecure Technologies" className="h-24 mx-auto mb-6" />
        <div className="border border-white/10 bg-white/[0.03] p-5 sm:p-8">
          {error ? (
            <div className="text-center py-6" data-testid="doc-request-expired">
              <AlertTriangle size={30} className="mx-auto text-red-400" />
              <h1 className="mt-4 text-white text-lg">Link not available</h1>
              <p className="mt-2 text-sm text-slate-400">{error}</p>
              <p className="mt-4 text-xs text-slate-500">Questions? Write to hr@nexsecure.in</p>
            </div>
          ) : !data ? (
            <p className="text-slate-400 text-sm text-center py-10">Loading…</p>
          ) : (
            <>
              <p className="text-[10px] tracking-[0.28em] uppercase text-nx-goldlight flex items-center gap-2"><FileText size={13} /> Documents / details required · HR Department</p>
              <h1 className="mt-3 text-white text-xl font-medium" data-testid="doc-request-name">Hello {data.employee_name}</h1>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                {allDone ? "Thank you — all requested items have been received and are with HR for verification." : `The following ${data.items.length === 1 ? "item is" : "items are"} missing from your employee records. Please provide ${data.items.length === 1 ? "it" : "them"} below.`}
              </p>
              <p className={`mt-4 text-xs flex items-center gap-2 ${allDone ? "text-emerald-300" : "text-amber-300"}`} data-testid="doc-request-expiry">
                <Clock size={13} /> {allDone ? "Completed." : `This link is valid for ${data.window} — until ${fmt(data.expires_at)}.`}
              </p>
              <div className="mt-6 flex flex-col gap-2" data-testid="doc-request-items">
                {data.items.map((it) => it.type === "text" ? <TextRow key={it.id} token={token} item={it} onDone={load} /> : <UploadRow key={it.id} token={token} item={it} onDone={load} />)}
              </div>
              <p className="mt-5 text-[11px] text-slate-500 leading-relaxed">
                Accepted: PDF, JPG, PNG or WEBP up to 10MB. Each upload is checked automatically to confirm it is the requested document.
                {!allDone && (data.auto_cancel ? " If the documents are not received before the link expires, the appointment will be cancelled automatically." : " If the documents are not received before the link expires, the matter is escalated to the CEO.")}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
